import React, { useState } from 'react'
import { useCurrency } from '../context/CurrencyContext'
import AddExpenseModal, { EXPENSE_CATEGORIES } from './AddExpenseModal'

const FREQ_TO_MONTHLY = { monthly: 1, quarterly: 1 / 3, 'half-yearly': 1 / 6, yearly: 1 / 12 }
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function toMonthly(amount, frequency) {
  return parseFloat(amount) * (FREQ_TO_MONTHLY[frequency] || 1)
}

function ord(n) {
  n = parseInt(n)
  const s = (n === 1 || n === 21 || n === 31) ? 'st' : (n === 2 || n === 22) ? 'nd' : (n === 3 || n === 23) ? 'rd' : 'th'
  return n + s
}

function fmtMonth(ym) {
  if (!ym) return ''
  const [y, m] = ym.split('-')
  return MONTHS[parseInt(m) - 1] + ' ' + y
}

function getCat(value) {
  return EXPENSE_CATEGORIES.find(c => c.value === value) || { label: value, icon: '📦', color: '#636e72' }
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────

function ExpenseDetailModal({ expense, payStatus, fmt, onClose }) {
  const now = new Date()
  const currentMonth = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0')
  const cat = getCat(expense.category)
  const monthly = toMonthly(expense.amount, expense.frequency)
  const st = payStatus

  const row = (label, value, valueColor) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.6rem 0', borderBottom: '1px solid var(--surface-hdr)' }}>
      <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '600', color: valueColor || 'var(--text)' }}>{value}</span>
    </div>
  )

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div style={{ background: 'var(--surface)', borderRadius: '10px', border: '1px solid var(--border)', width: '100%', maxWidth: '420px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700', marginBottom: '0.3rem' }}>
              {cat.icon} {expense.name}
            </div>
            <span style={{ display: 'inline-block', fontFamily: 'monospace', fontSize: '0.62rem', padding: '0.12rem 0.45rem', borderRadius: '3px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', background: cat.color + '18', color: cat.color, border: '1px solid ' + cat.color + '44' }}>
              {cat.label}
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1, padding: '0.2rem', marginLeft: '1rem' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '0.4rem 1.5rem 0.9rem' }}>
          {row('Amount', fmt(expense.amount))}
          {row('Frequency', expense.frequency.charAt(0).toUpperCase() + expense.frequency.slice(1))}
          {expense.frequency !== 'monthly' && row('Monthly Equivalent', '≈ ' + fmt(monthly) + '/mo', 'var(--text-muted)')}
          {row('Due Day', expense.due_day ? ord(expense.due_day) + ' of every month' : 'Not set')}
          {row('This Month', fmtMonth(currentMonth))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.6rem 0', borderBottom: '1px solid var(--surface-hdr)' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>Payment Status</span>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '600', color: st === 'paid' ? '#4a6741' : st === 'unpaid' ? '#b8441a' : 'var(--text-muted)' }}>
              {st === 'paid' ? '✓ Paid' : st === 'unpaid' ? '✗ Not Paid' : '— Not marked'}
            </span>
          </div>
          {expense.notes && (
            <div style={{ marginTop: '0.75rem', background: 'var(--surface-hdr)', borderRadius: '6px', padding: '0.7rem 0.9rem' }}>
              <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>Notes</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text)', lineHeight: 1.5 }}>{expense.notes}</div>
            </div>
          )}
        </div>

        <div style={{ padding: '0.8rem 1.5rem 1.2rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Expenses({ expenses, expensePayments, loans, insurancePolicies, onSave, onDelete, onSetPayment, currentMonth, loading }) {
  const { fmt } = useCurrency()
  const [showModal, setShowModal]       = useState(false)
  const [editingExpense, setEditingExpense] = useState(null)
  const [detailExpense, setDetailExpense]   = useState(null)
  const [expandedCats, setExpandedCats] = useState(
    Object.fromEntries(EXPENSE_CATEGORIES.map(c => [c.value, true]))
  )

  const now = new Date()
  const CML = MONTHS[now.getMonth()] + ' ' + now.getFullYear()

  const totalMonthlyExpenses = expenses.reduce((s, e) => s + toMonthly(e.amount, e.frequency), 0)
  const totalEMI = loans.reduce((s, l) => s + parseFloat(l.emi_amount || 0), 0)
  const totalInsurance = (insurancePolicies || []).reduce((s, p) => {
    const factor = FREQ_TO_MONTHLY[p.premium_frequency] || 1
    return s + parseFloat(p.premium_amount || 0) * factor
  }, 0)
  const grandTotal = totalMonthlyExpenses + totalEMI + totalInsurance

  function toggleCat(cat) { setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] })) }
  function openAdd()       { setEditingExpense(null); setShowModal(true) }
  function openEdit(exp)   { setEditingExpense(exp);  setShowModal(true) }
  function handleClose()   { setShowModal(false); setEditingExpense(null) }

  async function handleSave(data) {
    await onSave(editingExpense ? { ...data, id: editingExpense.id } : data)
    handleClose()
  }

  function getPayStatus(expenseId) {
    const p = (expensePayments || []).find(
      p => p.expense_id === expenseId && p.payment_month === currentMonth
    )
    return p ? p.status : null
  }

  if (loading) {
    return (
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.8rem' }}>
        Loading expenses...
      </div>
    )
  }

  const activeCats = new Set(expenses.map(e => e.category))

  return (
    <>
      {showModal && (
        <AddExpenseModal expense={editingExpense} onSave={handleSave} onClose={handleClose} />
      )}
      {detailExpense && (
        <ExpenseDetailModal
          expense={detailExpense}
          payStatus={getPayStatus(detailExpense.id)}
          fmt={fmt}
          onClose={() => setDetailExpense(null)}
        />
      )}

      <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

        {/* ── Summary cards ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
          {[
            { label: 'Monthly Expenses',      value: totalMonthlyExpenses, color: '#c9a84c', sub: `${expenses.length} item${expenses.length !== 1 ? 's' : ''}` },
            { label: 'Loan EMIs',             value: totalEMI,             color: '#b8441a', sub: `${loans.length} loan${loans.length !== 1 ? 's' : ''}` },
            { label: 'Insurance / Month',     value: totalInsurance,       color: '#3d4a5c', sub: `${(insurancePolicies || []).length} polic${(insurancePolicies || []).length !== 1 ? 'ies' : 'y'}` },
            { label: 'Total Outflow / Month', value: grandTotal,           color: '#7a5d1e', sub: 'expenses + EMI + insurance' },
          ].map(c => (
            <div key={c.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.1rem 1.3rem', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: c.color }} />
              <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>{c.label}</div>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: '700', color: c.color }}>{fmt(c.value)}</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{c.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Section header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.15rem', fontWeight: '700', marginBottom: '0.2rem' }}>Monthly Expenses</div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
              Track and organise your spending by category
            </div>
          </div>
          <button onClick={openAdd}
            style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            + Add Expense
          </button>
        </div>

        {/* ── Category accordion cards ── */}
        {EXPENSE_CATEGORIES.map(cat => {
          const items = expenses.filter(e => e.category === cat.value)
          const catMonthly = items.reduce((s, e) => s + toMonthly(e.amount, e.frequency), 0)
          const isExpanded = expandedCats[cat.value]

          return (
            <div key={cat.value} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>

              {/* Category header */}
              <div
                onClick={() => toggleCat(cat.value)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem 1.4rem', background: 'var(--surface-hdr)', cursor: 'pointer', transition: 'background 0.15s', borderBottom: isExpanded ? '1px solid var(--border)' : 'none' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-hdr)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>{cat.icon}</span>
                  <div>
                    <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.95rem', fontWeight: '700' }}>{cat.label}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                      {items.length} item{items.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {catMonthly > 0 && (
                    <span style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: '700', color: cat.color }}>
                      {fmt(catMonthly)}
                      <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', fontWeight: '400' }}>/mo</span>
                    </span>
                  )}
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', display: 'inline-block', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                </div>
              </div>

              {/* Items */}
              {isExpanded && (
                <div>
                  {items.length === 0 ? (
                    <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.72rem' }}>
                      No {cat.label.toLowerCase()} expenses yet.{' '}
                      <button onClick={openAdd}
                        style={{ background: 'none', border: 'none', color: cat.color, fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                        Add one
                      </button>
                    </div>
                  ) : (
                    items.map((expense, i) => {
                      const monthly = toMonthly(expense.amount, expense.frequency)
                      const st      = getPayStatus(expense.id)

                      return (
                        <div key={expense.id}
                          style={{ padding: '0.85rem 1.4rem', borderBottom: i < items.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.12s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.03)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                          {/* Top row: name + amount + payment indicator */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.3rem' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>{expense.name}</span>
                                {st === 'paid' && (
                                  <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: '#4a6741', fontWeight: '600' }}>✓ Paid</span>
                                )}
                                {st === 'unpaid' && (
                                  <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: '#b8441a', fontWeight: '600' }}>✗ Unpaid</span>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: '0.45rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.2rem' }}>
                                <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', background: 'var(--surface-hdr)', padding: '0.1rem 0.4rem', borderRadius: '3px', textTransform: 'capitalize' }}>
                                  {expense.frequency}
                                </span>
                                {expense.due_day && (
                                  <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>
                                    Due: {ord(expense.due_day)}
                                  </span>
                                )}
                                {expense.notes && (
                                  <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', fontStyle: 'italic' }} title={expense.notes}>
                                    📝 {expense.notes.length > 35 ? expense.notes.slice(0, 35) + '…' : expense.notes}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '1rem' }}>
                              <div style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: '700' }}>{fmt(expense.amount)}</div>
                              {expense.frequency !== 'monthly' && (
                                <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>≈ {fmt(monthly)}/mo</div>
                              )}
                            </div>
                          </div>

                          {/* Payment row — matches LoanList EMI row style */}
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem', paddingTop: '0.65rem', borderTop: '1px dashed var(--border)', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
                              Payment {CML}:
                            </span>
                            {['paid', 'unpaid'].map(s => (
                              <button key={s} onClick={() => onSetPayment(expense.id, s)}
                                style={{
                                  padding: '0.26rem 0.7rem', borderRadius: '20px', border: '1.5px solid',
                                  borderColor: st === s ? (s === 'paid' ? '#4a6741' : '#b8441a') : 'var(--border)',
                                  fontFamily: 'monospace', fontSize: '0.63rem', fontWeight: '500', cursor: 'pointer',
                                  background: st === s ? (s === 'paid' ? '#4a6741' : '#b8441a') : 'transparent',
                                  color: st === s ? '#fff' : 'var(--text-muted)',
                                  transition: 'all 0.15s'
                                }}>
                                {s === 'paid' ? '✓ Paid' : '✗ Not Paid'}
                              </button>
                            ))}
                            <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: '0.61rem', color: 'var(--text-muted)', background: 'var(--surface-hdr)', padding: '0.16rem 0.42rem', borderRadius: '3px' }}>
                              {CML}
                            </span>
                          </div>

                          {/* Action buttons */}
                          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                            <button onClick={() => setDetailExpense(expense)}
                              style={{ background: 'none', border: '1px solid var(--border)', padding: '0.2rem 0.55rem', borderRadius: '3px', fontSize: '0.65rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.12s' }}
                              onMouseEnter={e => { e.target.style.borderColor = '#3d4a5c'; e.target.style.color = '#3d4a5c' }}
                              onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-muted)' }}>
                              Detail
                            </button>
                            <button onClick={() => openEdit(expense)}
                              style={{ background: 'none', border: '1px solid var(--border)', padding: '0.2rem 0.55rem', borderRadius: '3px', fontSize: '0.65rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.12s' }}
                              onMouseEnter={e => { e.target.style.borderColor = 'var(--text)'; e.target.style.color = 'var(--text)' }}
                              onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-muted)' }}>
                              Edit
                            </button>
                            <button onClick={() => onDelete(expense.id)}
                              style={{ background: 'none', border: '1px solid var(--border)', padding: '0.2rem 0.55rem', borderRadius: '3px', fontSize: '0.65rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.12s' }}
                              onMouseEnter={e => { e.target.style.borderColor = '#c0392b'; e.target.style.color = '#c0392b' }}
                              onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-muted)' }}>
                              Remove
                            </button>
                          </div>

                        </div>
                      )
                    })
                  )}

                  {/* Category total footer */}
                  {items.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 1.4rem', background: 'var(--surface-hdr)', borderTop: '1px solid var(--border)' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.63rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {cat.label} Total / Month
                      </span>
                      <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '700', color: cat.color }}>
                        {fmt(catMonthly)}
                        <span style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'var(--text-muted)', fontWeight: '400', marginLeft: '0.2rem' }}>/mo</span>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}

        {/* ── Grand total breakdown ── */}
        {expenses.length > 0 && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.25rem 1.5rem', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#c9a84c' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', marginBottom: '0.2rem' }}>Grand Total Monthly Expenses</div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                  {expenses.length} item{expenses.length !== 1 ? 's' : ''} across {activeCats.size} categor{activeCats.size !== 1 ? 'ies' : 'y'}
                </div>
              </div>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: '2rem', fontWeight: '900', color: '#c9a84c' }}>
                {fmt(totalMonthlyExpenses)}
              </div>
            </div>
            <div style={{ height: '10px', borderRadius: '99px', overflow: 'hidden', display: 'flex', gap: '2px', marginBottom: '0.7rem' }}>
              {EXPENSE_CATEGORIES.map(cat => {
                const catMonthly = expenses.filter(e => e.category === cat.value)
                  .reduce((s, e) => s + toMonthly(e.amount, e.frequency), 0)
                const pct = totalMonthlyExpenses > 0 ? (catMonthly / totalMonthlyExpenses) * 100 : 0
                if (pct < 0.5) return null
                return (
                  <div key={cat.value}
                    style={{ height: '100%', width: pct + '%', background: cat.color, borderRadius: '2px', transition: 'width 0.5s ease', flexShrink: 0 }}
                    title={`${cat.label}: ${fmt(catMonthly)}`} />
                )
              })}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1rem' }}>
              {EXPENSE_CATEGORIES.filter(cat => activeCats.has(cat.value)).map(cat => {
                const catMonthly = expenses.filter(e => e.category === cat.value)
                  .reduce((s, e) => s + toMonthly(e.amount, e.frequency), 0)
                const pct = totalMonthlyExpenses > 0 ? Math.round((catMonthly / totalMonthlyExpenses) * 100) : 0
                return (
                  <span key={cat.value} style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: cat.color, display: 'inline-block', flexShrink: 0 }} />
                    {cat.icon} {cat.label}:{' '}
                    <span style={{ color: cat.color, fontWeight: '600' }}>{fmt(catMonthly)}</span>
                    <span>({pct}%)</span>
                  </span>
                )
              })}
            </div>
          </div>
        )}

        {/* ── Empty state ── */}
        {expenses.length === 0 && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '4rem 2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.8rem' }}>💸</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700', marginBottom: '0.4rem' }}>No expenses tracked yet</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.83rem', marginBottom: '1.25rem' }}>Add your monthly expenses to get a complete financial picture</div>
            <button onClick={openAdd}
              style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.55rem 1.2rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              + Add First Expense
            </button>
          </div>
        )}

      </div>
    </>
  )
}
